import { type Component, createSignal, Show } from 'solid-js';
import { useTranslation } from '@/i18n';
import { gameStore } from '@/stores/gameStore';
import { apiService } from '@/services/api';

/**
 * TeamPhotoUpload - Allows team to upload a photo visible to other teams
 *
 * Shows at the bottom of the dashboard with:
 * - "TAK WIDZĄ WASZ ZESPÓŁ:" header
 * - Photo preview if uploaded
 * - Upload button for camera/gallery access on mobile
 */
const TeamPhotoUpload: Component = () => {
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [successMessage, setSuccessMessage] = createSignal<string | null>(null);

  // Get current team's photo URL from participation data
  const currentPhotoUrl = () => gameStore.gameState.participation?.photo_url || null;

  const handleFileSelect = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];

    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError(t('teamPhoto.invalidFormat'));
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setError(t('teamPhoto.tooLarge'));
      return;
    }

    setError(null);
    setSuccessMessage(null);
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('photo', file);

      const response = await apiService.uploadTeamPhoto(formData);

      if (response.success) {
        setSuccessMessage(t('teamPhoto.saved'));
        // Refresh dashboard to get updated photo URL
        await gameStore.loadDashboard();
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(response.error || t('teamPhoto.uploadError'));
      }
    } catch (err: any) {
      console.error('Photo upload error:', err);
      setError(err.message || t('teamPhoto.uploadError'));
    } finally {
      setIsUploading(false);
      // Clear input to allow selecting the same file again
      target.value = '';
    }
  };

  return (
    <div class="card mt-6">
      <div class="card-body">
        <div class="text-center space-y-4">
          {/* Header */}
          <div class="border-b border-white/10 pb-4">
            <h3 class="text-lg font-bold text-white/90 flex items-center justify-center gap-2">
              <span class="text-2xl">📸</span>
              {t('teamPhoto.header')}
            </h3>
            <p class="text-sm text-white/50 mt-1">
              {t('teamPhoto.visibleInRanking')}
            </p>
          </div>

          {/* Photo display area */}
          <div class="py-4">
            <Show
              when={currentPhotoUrl()}
              fallback={
                /* Placeholder when no photo */
                <div class="bg-white/5 rounded-2xl border-2 border-dashed border-white/20 p-8 max-w-sm mx-auto">
                  <div class="text-6xl mb-3 opacity-50">🤳</div>
                  <p class="text-white/40 text-sm">
                    {t('teamPhoto.noPhoto')}
                  </p>
                </div>
              }
            >
              {/* Photo preview */}
              <div class="max-w-sm mx-auto rounded-2xl overflow-hidden border-2 border-emerald-500/30 shadow-lg shadow-emerald-500/10">
                <img
                  src={currentPhotoUrl()!}
                  alt={t('teamPhoto.photoAlt')}
                  class="w-full h-auto object-cover"
                />
              </div>
            </Show>
          </div>

          {/* Error message */}
          <Show when={error()}>
            <div class="bg-red-500/20 text-red-300 rounded-lg px-4 py-2 text-sm">
              {error()}
            </div>
          </Show>

          {/* Success message */}
          <Show when={successMessage()}>
            <div class="bg-emerald-500/20 text-emerald-300 rounded-lg px-4 py-2 text-sm">
              {successMessage()}
            </div>
          </Show>

          {/* Upload button */}
          <div class="pt-2">
            <label class="relative inline-block cursor-pointer">
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                disabled={isUploading()}
                class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <div
                class={`px-6 py-3 rounded-xl font-semibold text-white transition-all ${
                  isUploading()
                    ? 'bg-gray-500 cursor-wait'
                    : currentPhotoUrl()
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:shadow-lg hover:shadow-blue-500/30 hover:scale-105'
                    : 'bg-gradient-to-r from-emerald-500 to-cyan-500 hover:shadow-lg hover:shadow-emerald-500/30 hover:scale-105'
                }`}
              >
                {isUploading() ? (
                  <span class="flex items-center gap-2">
                    <span class="animate-spin">⏳</span>
                    {t('teamPhoto.uploading')}
                  </span>
                ) : currentPhotoUrl() ? (
                  <span class="flex items-center gap-2">
                    <span>📷</span>
                    {t('teamPhoto.changePhoto')}
                  </span>
                ) : (
                  <span class="flex items-center gap-2">
                    <span>📸</span>
                    {t('teamPhoto.addPhoto')}
                  </span>
                )}
              </div>
            </label>
            <p class="text-xs text-white/30 mt-2">
              {t('teamPhoto.cameraOrGallery')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamPhotoUpload;
